import React from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router';

import {
  Modal,
  ModalActions,
  ModalCancelButton,
  ModalHeader,
  ModalSubmitButton,
  showModal,
} from '@edna/components';
import {copyToClipBoard} from '@edna/utils';

import {SecondaryText} from 'src/components/primitives';
import {Banner} from 'src/containers/Banner';
import {MagicianImage} from 'src/images';
import PATHS from 'src/paths';

import {useCreateInviteLinkMutation} from './api';
import {ELeadClientStatus, ELeadType, TAgentClient, TLeadClient} from './definitions';
import * as S from './style';

type TProps = {
  closeModal: TEmptyFunction;
};

const CopyInviteLinkModal = React.memo<TProps & {inviteLink: string; isAgent?: boolean}>(
  ({closeModal, inviteLink, isAgent}) => {
    const {t} = useTranslation();
    const navigate = useNavigate();

    const handleCloseModal = React.useCallback(() => {
      navigate(isAgent ? PATHS.COMPANY_AGENT_CLIENTS : PATHS.COMPANY_CLIENTS);
      closeModal();
    }, [navigate, closeModal, isAgent]);

    const postfix = isAgent ? 'Agent' : '';

    return (
      <Modal width="450px">
        <ModalHeader>
          {t(`LeadCompanyClients:copyInviteLinkModal.inviteLink${postfix}`)}
        </ModalHeader>
        <SecondaryText bottomIndent="5">
          {t(`LeadCompanyClients:copyInviteLinkModal.copyLink${postfix}`)}
        </SecondaryText>
        <Banner type="WARNING" title={t('LeadCompanyClients:copyInviteLinkModal.linkIsValid')} />
        <SecondaryText>{t('LeadCompanyClients:copyInviteLinkModal.link')}</SecondaryText>
        <S.StartRow>
          <S.Textarea readOnly resize={false} value={inviteLink} rows={3} />
          <S.CopyIcon onClick={() => copyToClipBoard(inviteLink)} />
        </S.StartRow>
        <ModalActions>
          <ModalSubmitButton onClick={handleCloseModal}>
            {t('LeadCompanyClients:copyInviteLinkModal.wellDone')}
          </ModalSubmitButton>
        </ModalActions>
      </Modal>
    );
  },
);

CopyInviteLinkModal.displayName = 'CopyInviteLinkModal';

export const showCopyInviteLinkModal = (inviteLink: string, isAgent?: boolean) =>
  showModal((closeInviteLinkModal) => (
    <CopyInviteLinkModal
      closeModal={closeInviteLinkModal}
      inviteLink={inviteLink}
      isAgent={isAgent}
    />
  ));

const CreateInviteLinkModal = React.memo<
  TProps & {leadClient: TLeadClient | TAgentClient; isAgent?: boolean}
>(({closeModal, leadClient, isAgent: isAgentProp}) => {
  const {t} = useTranslation();

  const [createInviteLink, {data: inviteLink, isLoading}] = useCreateInviteLinkMutation();

  const isAgent = isAgentProp || leadClient.type === ELeadType.AGENT;

  React.useEffect(() => {
    if (inviteLink) {
      closeModal();
      showCopyInviteLinkModal(inviteLink.inviteUrl, isAgent);
    }
  }, [inviteLink, isAgent]);

  const handleSubmit = React.useCallback(() => {
    createInviteLink(leadClient);
  }, [createInviteLink, leadClient]);

  const postfix = isAgent ? 'Agent' : '';

  return (
    <Modal width="430px">
      <ModalHeader>{t(`LeadCompanyClients:createInviteLinkModal.title${postfix}`)}</ModalHeader>
      <SecondaryText bottomIndent="5">
        {t(`LeadCompanyClients:createInviteLinkModal.text${postfix}`)}
      </SecondaryText>
      {leadClient.status === ELeadClientStatus.REQUIRES_ATTENTION && (
        <Banner type="WARNING" fontSize="S" shadowSize="XS" noMargin>
          {t('LeadCompanyClients:createInviteLinkModal.tariffsWarning')}
        </Banner>
      )}
      <S.ModalCenterImage as={MagicianImage} />
      <ModalActions>
        <ModalCancelButton data-selector="ModalCancelButton" onClick={closeModal}>
          {t('Common:button.cancel')}
        </ModalCancelButton>
        <ModalSubmitButton disabled={isLoading} onClick={handleSubmit}>
          {t('Common:button.create')}
        </ModalSubmitButton>
      </ModalActions>
    </Modal>
  );
});

CreateInviteLinkModal.displayName = 'CreateInviteLinkModal';

export const showCreateInviteLinkModal = (
  leadClient: TLeadClient | TAgentClient,
  isAgent?: boolean,
) =>
  showModal((closeModal) => (
    <CreateInviteLinkModal closeModal={closeModal} leadClient={leadClient} isAgent={isAgent} />
  ));
